import type { ChartOptions } from "chart.js";
import type { LegacyAxisScale, LegacyChartOptions } from "../types";

export type LegendClickHandler = (
  event: unknown,
  item: { text?: string; datasetIndex?: number },
  legend: { chart: { isDatasetVisible: (index: number) => boolean; setDatasetVisibility: (index: number, visible: boolean) => void; update: () => void } }
) => void;

type ScaleEntry = [string, Record<string, unknown>];

function mapGridLines(axis: LegacyAxisScale): Record<string, unknown> {
  const gridLines = axis.gridLines ?? {};
  return {
    color: gridLines.color,
    display: gridLines.drawOnChartArea ?? true,
    drawBorder: gridLines.drawBorder,
    drawTicks: gridLines.drawTicks
  };
}

function getAxisId(axis: LegacyAxisScale, axisType: "x" | "y", index: number): string {
  return axis.id ?? `${axisType}-axis-${index + 1}`;
}

function mapScale(axis: LegacyAxisScale, axisType: "x" | "y", index: number): ScaleEntry {
  const axisId = getAxisId(axis, axisType, index);
  const rawTime = (axis.time ?? {}) as Record<string, unknown>;
  const rawTicks = (axis.ticks ?? {}) as Record<string, unknown>;
  const min = rawTime.min ?? rawTicks.min;
  const max = rawTime.max ?? rawTicks.max;
  const axisOptions: Record<string, unknown> = {
    type: axis.type ?? "linear",
    display: axis.display ?? true,
    position: axis.position,
    min,
    max,
    grid: mapGridLines(axis),
    ticks: {
      ...rawTicks,
      display: rawTicks.display ?? true,
      stepSize: rawTicks.stepSize,
      maxRotation: rawTicks.maxRotation,
      minRotation: rawTicks.minRotation,
      padding: rawTicks.padding
    },
    title: {
      display: axis.scaleLabel?.display ?? false,
      text: axis.scaleLabel?.labelString
    }
  };

  if ((axis.type ?? "") === "time") {
    axisOptions.type = "time";
    axisOptions.time = {
      unit: rawTime.unit,
      stepSize: rawTime.stepSize,
      tooltipFormat: rawTime.tooltipFormat,
      minUnit: rawTime.minUnit,
      isoWeekday: rawTime.isoWeekday,
      displayFormats: rawTime.displayFormats
    };
  }

  if (axis.afterFit) {
    // Keep legacy afterFit callbacks for scale width/padding tuning.
    axisOptions.afterFit = axis.afterFit;
  }

  return [axisId, axisOptions];
}

function mapScales(legacy: LegacyChartOptions): Record<string, unknown> {
  const scales: Record<string, unknown> = {};
  const xAxes = legacy.scales?.xAxes ?? [];
  const yAxes = legacy.scales?.yAxes ?? [];

  xAxes.forEach((axis, index) => {
    const [id, options] = mapScale(axis, "x", index);
    scales[id] = options;
  });

  yAxes.forEach((axis, index) => {
    const [id, options] = mapScale(axis, "y", index);
    scales[id] = options;
  });

  if (Object.keys(scales).length === 0) {
    scales.x = { type: "linear" };
    scales.y = { type: "linear" };
  }

  return scales;
}

function mapAnnotation(legacy: LegacyChartOptions): Record<string, unknown> | undefined {
  const annotations = legacy.annotation?.annotations;
  if (!Array.isArray(annotations) || annotations.length === 0) {
    return undefined;
  }

  const mapped: Record<string, unknown> = {};
  annotations.forEach((annotation, index) => {
    const label = (annotation.label ?? {}) as Record<string, unknown>;
    mapped[`ann-${index}`] = {
      ...annotation,
      label: {
        ...label,
        display: true,
        content: label.content
      }
    };
  });

  return {
    annotations: mapped
  };
}

function mapTooltip(legacy: LegacyChartOptions): Record<string, unknown> {
  const tooltips = legacy.tooltips ?? {};
  return {
    enabled: (tooltips.enabled as boolean | undefined) ?? true,
    callbacks: tooltips.callbacks as Record<string, unknown> | undefined
  };
}

function defaultLegendClick(
  _event: unknown,
  item: { datasetIndex?: number },
  legend: { chart: { isDatasetVisible: (index: number) => boolean; setDatasetVisibility: (index: number, visible: boolean) => void; update: () => void } }
): void {
  const datasetIndex = item.datasetIndex;
  if (datasetIndex === undefined) {
    return;
  }

  const chart = legend.chart;
  chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
  chart.update();
}

export function convertLegacyOptions(
  legacy: LegacyChartOptions,
  onLegendClick?: LegendClickHandler
): ChartOptions<"scatter"> {
  return {
    responsive: true,
    maintainAspectRatio: legacy.maintainAspectRatio,
    aspectRatio: legacy.aspectRatio,
    layout: {
      ...(legacy.layout ?? {})
    },
    scales: mapScales(legacy) as ChartOptions<"scatter">["scales"],
    plugins: {
      title: {
        ...(legacy.title ?? {})
      },
      legend: {
        ...(legacy.legend ?? {}),
        onClick: onLegendClick ?? defaultLegendClick
      },
      tooltip: mapTooltip(legacy),
      annotation: mapAnnotation(legacy)
    }
  };
}
